import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Button from '../../../components/ui/Button';
import authService from '../../../services/authService';
import VerificationInputs from './components/VerificationInputs';
import CountdownTimer from './components/CountdownTimer';
import ResendCodeButton from './components/ResendCodeButton';
import SuccessAnimation from './components/SuccessAnimation';

const Verify = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [error, setError] = useState('');
  const [registrationData, setRegistrationData] = useState(null);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes in seconds
  const [canResend, setCanResend] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);

  // Load registration data and email from previous step
  useEffect(() => {
    const savedData = localStorage.getItem('registrationData');
    const savedEmail = localStorage.getItem('verificationEmail');

    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        setRegistrationData(parsedData);
        setVerificationEmail(parsedData.email || savedEmail || '');
      } catch (error) {
        console.error('Error parsing registration data:', error);
        navigate('/auth/register');
      }
    } else if (savedEmail) {
      // If we have just the email (from a direct registration call)
      setVerificationEmail(savedEmail);
      setRegistrationData({ email: savedEmail });
    } else {
      navigate('/auth/register');
    }
  }, [navigate]);

  // Timer effect
  useEffect(() => {
    if (timeLeft > 0 && !isVerified) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0) {
      setCanResend(true);
    }
  }, [timeLeft, isVerified]);

  const handleOtpChange = (index, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      setError('');

      // Auto-focus next input
      if (value && index < 5) {
        const nextInput = document.getElementById(`otp-${index + 1}`);
        if (nextInput) {
          nextInput.focus();
        }
      }

      // Auto-verify when all fields are filled
      if (newOtp.every(digit => digit !== '') && newOtp.join('').length === 6) {
        handleVerify(newOtp.join(''));
      }
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      if (prevInput) {
        prevInput.focus();
      }
    }
  };

  const handleVerify = async (otpValue = otp.join('')) => {
    if (otpValue.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }

    setIsVerifying(true);
    setError('');

    try {
      console.log('Verifying OTP for email:', verificationEmail, 'with code:', otpValue);

      // Call the actual verification API
      const response = await authService.verifyOTP(verificationEmail, otpValue);

      console.log('Verification response:', response);

      if (response.success) {
        setIsVerified(true);

        // Clear registration data from localStorage
        localStorage.removeItem('registrationData');
        localStorage.removeItem('verificationEmail');

        // Show success animation and redirect
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      } else {
        setError(response.message || 'Invalid verification code. Please try again.');
      }

    } catch (error) {
      console.error('Verification error:', error);
      console.error('Verification error response:', error.response?.data);
      console.error('Verification error status:', error.response?.status);

      // Handle different error types
      if (error.response?.data?.detail) {
        setError(error.response.data.detail);
      } else if (error.response?.data?.error) {
        setError(error.response.data.error);
      } else if (error.response?.data?.code) {
        setError(error.response.data.code[0]);
      } else if (error.response?.status === 404) {
        setError('User not found. Please go back and register again.');
      } else if (error.response?.status === 429) {
        setError('Too many verification attempts. Please wait before trying again.');
      } else if (error.response?.status >= 500) {
        setError('Server error. Please try again later or contact support.');
      } else if (error.response?.status === 400) {
        setError('Invalid verification code format. Please enter a 6-digit code.');
      } else {
        setError('Verification failed. Please check your code and try again.');
      }
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendCode = async () => {
    setResendLoading(true);
    setError('');

    try {
      console.log('Resending verification code to:', verificationEmail);

      // Call the actual resend verification API
      const response = await authService.resendVerification(verificationEmail);

      console.log('Resend response:', response);

      if (response.success) {
        // Reset timer and disable resend
        setTimeLeft(300);
        setCanResend(false);
        setOtp(['', '', '', '', '', '']);

        // Show success message
        setError(''); // Clear any previous errors

        // Focus first input
        const firstInput = document.getElementById('otp-0');
        if (firstInput) {
          firstInput.focus();
        }

        // Show temporary success message
        const successEl = document.createElement('div');
        successEl.className = 'fixed top-4 right-4 bg-green-500 text-white p-3 rounded-lg shadow-lg z-50';
        successEl.textContent = 'Verification code sent! Check your email.';
        document.body.appendChild(successEl);
        setTimeout(() => {
          if (document.body.contains(successEl)) {
            document.body.removeChild(successEl);
          }
        }, 3000);
      } else {
        setError(response.message || 'Failed to resend code. Please try again.');
      }

    } catch (error) {
      console.error('Resend error:', error);

      // Handle different error types
      if (error.response?.data?.detail) {
        setError(error.response.data.detail);
      } else if (error.response?.data?.error) {
        setError(error.response.data.error);
      } else if (error.response?.status === 429) {
        setError('Too many requests. Please wait a moment before trying again.');
      } else if (error.response?.status === 404) {
        setError('User not found. Please go back and register again.');
      } else {
        setError('Failed to send verification code. Please check your internet connection and try again.');
      }
    } finally {
      setResendLoading(false);
    }
  };

  const handleBackToRegistration = () => {
    navigate('/auth/country-language-selection');
  };

  if (!registrationData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-secondary-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-accent-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (isVerified) {
    return <SuccessAnimation />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-secondary-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img
              src="/assets/images/Giftsy-Logo-Final-1751671984839.png"
              alt="Giftsy Modern"
              className="h-16 w-auto"
            />
          </div>
          <h1 className="text-2xl font-heading font-bold text-accent-800 mb-2">
            Verify Your Email
          </h1>
          <p className="text-accent-600 text-base">
            Step 3: Enter the verification code sent to your email
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="mb-6">
          <div className="flex items-center justify-center">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 rounded-full bg-primary-600 flex items-center justify-center">
                <span className="text-white text-sm font-medium">✓</span>
              </div>
              <div className="w-12 h-0.5 bg-primary-600"></div>
              <div className="w-8 h-8 rounded-full bg-primary-600 flex items-center justify-center">
                <span className="text-white text-sm font-medium">✓</span>
              </div>
              <div className="w-12 h-0.5 bg-primary-600"></div>
              <div className="w-8 h-8 rounded-full bg-primary-600 flex items-center justify-center">
                <span className="text-white text-sm font-medium">3</span>
              </div>
            </div>
          </div>
          <div className="flex justify-between text-xs text-accent-600 mt-2 max-w-xs mx-auto">
            <span>Name & Email</span>
            <span>Country & Language</span>
            <span>Verification</span>
          </div>
        </div>

        {/* Email Info */}
        <div className="text-center mb-6">
          <p className="text-accent-600 text-sm">
            We sent a 6-digit verification code to
          </p>
          <p className="text-accent-800 font-medium text-base mt-1">
            {verificationEmail || registrationData?.email}
          </p>



          {/* Help Text */}
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-xs">
            <p className="text-yellow-800">
              <strong>Don't see the email?</strong> Check your spam folder or try resending below.
              Email delivery can take up to 5 minutes.
            </p>
          </div>
        </div>

        {/* Verification Card */}
        <div className="bg-white rounded-xl shadow-soft p-6 mb-6">
          {/* OTP Inputs */}
          <div className="mb-6">
            <VerificationInputs
              otp={otp}
              onChange={handleOtpChange}
              onKeyDown={handleKeyDown}
              error={error}
              disabled={isVerifying}
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-error-50 border border-error-200 rounded-lg">
              <p className="text-error text-sm font-medium">{error}</p>
            </div>
          )}

          {/* Countdown Timer */}
          <CountdownTimer timeLeft={timeLeft} />

          {/* Resend Code Button */}
          <ResendCodeButton
            canResend={canResend}
            onResend={handleResendCode}
            loading={resendLoading}
          />

          {/* Verify Button */}
          <Button
            onClick={() => handleVerify()}
            disabled={otp.some(digit => digit === '') || isVerifying}
            loading={isVerifying}
            fullWidth
            variant="primary"
            size="lg"
            className="mt-6"
          >
            {isVerifying ? 'Verifying...' : 'Verify Email'}
          </Button>

          {/* Back Button */}
          <div className="text-center mt-4">
            <button
              onClick={handleBackToRegistration}
              className="text-accent-600 hover:text-primary-600 text-sm font-medium transition-colors"
            >
              ← Back to Country Selection
            </button>
          </div>
        </div>

        {/* Help Text */}
        <div className="text-center text-xs text-accent-500 max-w-sm mx-auto">
          <div className="bg-accent-50 border border-accent-200 rounded-lg p-4 mb-4">
            <h4 className="font-medium text-accent-700 mb-2">Don't see the verification email?</h4>
            <div className="text-left space-y-2 text-accent-600">
              <p>• Check your spam/junk folder</p>
              <p>• Make sure you entered the correct email address</p>
              <p>• Try the "Resend Code" button above</p>
              <p>• Wait up to 5 minutes for delivery</p>
            </div>
            {canResend && (
              <button
                onClick={handleResendCode}
                disabled={resendLoading}
                className="mt-3 w-full bg-primary-600 text-white py-2 px-4 rounded-md text-sm font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {resendLoading ? 'Sending...' : 'Resend Verification Code'}
              </button>
            )}
          </div>
          <p>
            For security, this code will expire in {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')} minutes.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Verify;